import { Col, Row } from '@amzn/stencil-react-components/layout';
import { Text } from '@amzn/stencil-react-components/text';
import { IconBookmarkSmall } from '@amzn/stencil-react-icons';

import type { ScoreFacts, Strength, Strengths } from 'src/types/starlight/AuditInsights';
import { SuccessIcon } from 'src/v2/shared/ui/SuccessIcon';
import { SectionHeading } from './SectionHeading';
import { edgeLine } from './panelEdges';

/** Green marks the section's tone, matching the pass glyph on its heading. */
const STRENGTH_EDGE = edgeLine('color.status.success-border-default');

interface StrengthsSectionProps {
  strengths: Strengths | undefined;
  score: ScoreFacts;
  heading: string;
  /** Renders the pass count note. Called only when the audit was scored. */
  formatPassCount: (passed: number, total: number) => string;
}

/**
 * What held up on the audit: a one-line reading, then the specific behaviors behind it.
 *
 * Renders nothing when generation produced neither an overview nor an item, so a partial
 * insight omits the section rather than showing an empty heading.
 */
export function StrengthsSection({
  strengths,
  score,
  heading,
  formatPassCount,
}: StrengthsSectionProps): JSX.Element | null {
  const items = (strengths?.items ?? []).filter(hasSomethingToSay);

  if (!strengths || (!strengths.overview && items.length === 0)) {
    return null;
  }

  return (
    <Col gridGap="dimensions.spacing.200">
      <SectionHeading
        icon={<SuccessIcon />}
        label={heading}
        // Absent passCount means the audit completed unscored, so there is no ratio to
        // quote. Zero is a scored result and still reads.
        {...(score.passCount === undefined
          ? {}
          : { badge: formatPassCount(score.passCount, score.totalScored) })}
      />
      {strengths.overview ? <Text>{strengths.overview}</Text> : null}
      {items.length > 0 ? (
        <Col gridGap="dimensions.spacing.200">
          {items.map((item) => (
            // Keyed on what the card shows, not on the question ids alone: an item earns its
            // card on its behaviour text, so it can arrive citing no questions, and two
            // items can credit the same ones. Ids alone then repeat a key and React
            // reconciles one card's content onto another.
            <StrengthCard key={strengthKey(item)} item={item} />
          ))}
        </Col>
      ) : null}
    </Col>
  );
}

/**
 * Identifies a card by the wording it shows.
 *
 * Two items are the same card only when their behaviour, area and credited questions all
 * agree, at which point they draw identically anyway.
 */
function strengthKey(item: Strength): string {
  return [item.behavior, item.processPath, item.questionIds.join('-')].join('|');
}

/** True when the item carries prose. An item with only question ids has nothing to show. */
function hasSomethingToSay(item: Strength): boolean {
  return !!item.behavior;
}

/**
 * One thing that held up, as a card.
 *
 * Same shape as `FailureThemesSection`'s theme card — tinted fill, outline, radius and
 * padding all match — so the two halves of the reading scan alike and only the tone tells
 * them apart. The section heading already carries the pass glyph, so repeating it per card
 * would say "passed" twice.
 *
 * Deliberately not accented down its left edge: in this tab that marks a quotation, and a
 * list of findings is not one. See `panelEdges`.
 */
function StrengthCard({ item }: { item: Strength }): JSX.Element {
  return (
    <Col
      backgroundColor="color.status.success-bg"
      borderRadius="dimensions.border.radius.200"
      padding="dimensions.spacing.300"
      gridGap="dimensions.spacing.100"
      minWidth={0}
      css={STRENGTH_EDGE}
    >
      <Text fontWeight="bold" fontSize="T200">
        {item.behavior}
      </Text>
      {/* Under the behavior rather than opposite it, and plain rather than on a pill: the
          path labels what was done well and is not a second finding competing with it. A
          theme card pills it instead, because there it shares a row with the question ids
          it has to be told apart from. */}
      {item.processPath ? (
        <Row alignItems="center" gridGap="dimensions.spacing.100">
          <IconBookmarkSmall color="color.text.secondary" aria-hidden />
          <Text fontSize="T50" color="color.text.secondary">
            {item.processPath}
          </Text>
        </Row>
      ) : null}
    </Col>
  );
}
